<template>
  <div class="border-solid border-2 p-4 text-left">
    <b>{{ $t("bodyprint_declined.actions.title") }}</b>

    <div class="flex flex-col gap-2 mt-3">
      <button
        :class="optionButtonClass('restore')"
        @click="activeAction = 'restore'"
      >
        {{ $t("bodyprint_declined.actions.options.restore") }}
      </button>
      <button
        :class="optionButtonClass('transfer')"
        @click="activeAction = 'transfer'"
      >
        {{ $t("bodyprint_declined.actions.options.transfer") }}
      </button>
      <button
        :class="optionButtonClass('remove')"
        @click="activeAction = 'remove'"
      >
        {{ $t("bodyprint_declined.actions.options.remove") }}
      </button>
    </div>

    <div v-if="activeAction === 'restore'" class="mt-4 border-t">
      <p class="py-4">
        {{
          $t("bodyprint_declined.actions.restore.description", [
            item.user ? item.user.email : "-"
          ])
        }}
      </p>
      <button
        class="h-10 text-md px-3 mt-3 text-white bg-blue-800"
        :disabled="loading"
        @click="$emit('restore')"
      >
        {{ $t("bodyprint_declined.actions.restore.button") }}
      </button>
    </div>

    <div v-else-if="activeAction === 'transfer'" class="mt-4 border-t">
      <div class="flex flex-row gap-2 py-4 mb-3">
        <button
          :class="transferModeButtonClass('existing')"
          @click="transferMode = 'existing'"
        >
          {{ $t("bodyprint_declined.actions.transfer.existing_user") }}
        </button>
        <button
          :class="transferModeButtonClass('new')"
          @click="transferMode = 'new'"
        >
          {{ $t("bodyprint_declined.actions.transfer.new_user") }}
        </button>
      </div>

      <div class="flex flex-col items-start mb-2">
        <label
          for="transfer_email"
          class="block font-medium text-sm text-gray-700"
          >{{
            $t("bodyprint_declined.actions.transfer.input.email.label")
          }}</label
        >
        <input
          id="transfer_email"
          type="text"
          v-model="transferForm.email"
          :placeholder="
            $t('bodyprint_declined.actions.transfer.input.email.placeholder')
          "
          class="w-full mt-1 p-2 border-2 border-solid border-black rounded-md"
        />
      </div>

      <template v-if="transferMode === 'new'">
        <div class="flex flex-col items-start mb-2">
          <label
            for="transfer_first_name"
            class="block font-medium text-sm text-gray-700"
            >{{
              $t("bodyprint_declined.actions.transfer.input.first_name.label")
            }}</label
          >
          <input
            id="transfer_first_name"
            type="text"
            v-model="transferForm.first_name"
            :placeholder="
              $t(
                'bodyprint_declined.actions.transfer.input.first_name.placeholder'
              )
            "
            class="w-full mt-1 p-2 border-2 border-solid border-black rounded-md"
          />
        </div>
        <div class="flex flex-col items-start mb-2">
          <label
            for="transfer_last_name"
            class="block font-medium text-sm text-gray-700"
            >{{
              $t("bodyprint_declined.actions.transfer.input.last_name.label")
            }}</label
          >
          <input
            id="transfer_last_name"
            type="text"
            v-model="transferForm.last_name"
            :placeholder="
              $t(
                'bodyprint_declined.actions.transfer.input.last_name.placeholder'
              )
            "
            class="w-full mt-1 p-2 border-2 border-solid border-black rounded-md"
          />
        </div>
        <div class="flex flex-col items-start mb-2">
          <label
            for="transfer_date_of_birth"
            class="block font-medium text-sm text-gray-700"
            >{{
              $t(
                "bodyprint_declined.actions.transfer.input.date_of_birth.label"
              )
            }}</label
          >
          <input
            id="transfer_date_of_birth"
            type="date"
            v-model="transferForm.date_of_birth"
            class="w-full mt-1 p-2 border-2 border-solid border-black rounded-md"
          />
        </div>
      </template>

      <button
        class="h-10 text-md px-3 mt-3 text-white"
        :class="canSubmitTransfer ? 'bg-blue-800' : 'bg-gray-600'"
        :disabled="!canSubmitTransfer || loading"
        @click="submitTransfer"
      >
        {{ $t("bodyprint_declined.actions.transfer.button") }}
      </button>
    </div>

    <div v-else-if="activeAction === 'remove'" class="mt-4 pt-4 border-t">
      <p class="py-4">
        {{ $t("bodyprint_declined.actions.remove.description") }}
      </p>
      <button
        class="h-10 text-md px-3 mt-3 text-white bg-red-700"
        :disabled="loading"
        @click="showRemoveDialog = true"
      >
        {{ $t("bodyprint_declined.actions.remove.button") }}
      </button>
    </div>

    <dialog-confirm
      v-if="showRemoveDialog"
      color="red"
      :title="$t('bodyprint_declined.actions.remove.dialog.title').toString()"
      :message="
        $t('bodyprint_declined.actions.remove.dialog.message').toString()
      "
      @confirmed="
        showRemoveDialog = false;
        $emit('remove');
      "
      @close="showRemoveDialog = false"
    />
  </div>
</template>

<script>
import DialogConfirm from "@/components/dialog.vue";

export default {
  name: "BodyPrintDeclinedActions",
  components: { DialogConfirm },
  props: {
    item: {
      type: Object,
      required: true
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      activeAction: null,
      transferMode: "existing",
      transferForm: {
        email: "",
        first_name: "",
        last_name: "",
        date_of_birth: ""
      },
      showRemoveDialog: false
    };
  },
  computed: {
    canSubmitTransfer() {
      if (this.transferForm.email === "") return false;

      if (this.transferMode === "new") {
        return (
          this.transferForm.first_name !== "" &&
          this.transferForm.last_name !== "" &&
          this.transferForm.date_of_birth !== ""
        );
      }

      return true;
    }
  },
  watch: {
    item() {
      this.activeAction = null;
      this.transferMode = "existing";
      this.transferForm = {
        email: "",
        first_name: "",
        last_name: "",
        date_of_birth: ""
      };
    }
  },
  methods: {
    optionButtonClass(action) {
      return [
        "h-10 text-md px-3 border-2 border-solid border-black",
        this.activeAction === action ? "bg-blue-800 text-white" : "bg-white"
      ];
    },
    transferModeButtonClass(mode) {
      return [
        "h-10 text-md px-3 border-2 border-solid border-black",
        this.transferMode === mode ? "bg-blue-800 text-white" : "bg-white"
      ];
    },
    submitTransfer() {
      if (!this.canSubmitTransfer) return;

      const payload =
        this.transferMode === "new"
          ? { ...this.transferForm }
          : { email: this.transferForm.email };

      this.$emit("transfer", payload);
    }
  }
};
</script>
